import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import billingService from '../services/billingService';
import botService from '../services/botService';
import RefreshButton from '../components/RefreshButton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaperPlane } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { Line } from 'react-chartjs-2';
import DatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import ptBR from 'date-fns/locale/pt-BR';
import MoonLoader from "react-spinners/MoonLoader";

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import './Billing.css';

// Registra o locale pt-BR para o DatePicker
registerLocale('pt-BR', ptBR);

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const Billing = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [monthlyBilling, setMonthlyBilling] = useState(null);
  const [billingData, setBillingData] = useState(null);
  const [chartData, setChartData] = useState(null);
  const [bots, setBots] = useState([]);
  const [resendingLink, setResendingLink] = useState({});
  const [currencyBreakdown, setCurrencyBreakdown] = useState(null);
  const [countryBreakdown, setCountryBreakdown] = useState(null);

  // Filtros
  const [filters, setFilters] = useState({
    start_date: '',
    end_date: '',
    bot_id: '',
    payment_method: '',
    gateway: ''
  });

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (filters.start_date || filters.end_date || filters.bot_id || filters.payment_method || filters.gateway) {
      loadBillingData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      const [monthly, chart, botsData, currencyData, countryData] = await Promise.all([
        billingService.getMonthlyBilling(),
        billingService.getChartData(12),
        botService.getAllBots(),
        billingService.getCurrencyBreakdown().catch(() => ({ data: [] })),
        billingService.getCountryBreakdown().catch(() => ({ data: [] })),
      ]);

      setMonthlyBilling(monthly);
      setChartData(chart.data);
      setBots(botsData);
      setCurrencyBreakdown(currencyData.data || []);
      setCountryBreakdown(countryData.data || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao carregar dados de faturamento');
    } finally {
      setLoading(false);
    }
  };

  const loadBillingData = async () => {
    try {
      setLoading(true);
      const data = await billingService.getBilling(filters);
      setBillingData(data);
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao carregar dados filtrados');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    await loadInitialData();
    if (filters.start_date || filters.end_date || filters.bot_id || filters.payment_method || filters.gateway) {
      await loadBillingData();
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      start_date: '',
      end_date: '',
      bot_id: '',
      payment_method: '',
      gateway: ''
    });
    setBillingData(null);
  };

  const handleResendGroupLink = async (transactionId) => {
    try {
      setResendingLink(prev => ({ ...prev, [transactionId]: true }));
      setError('');
      setSuccess('');
      
      const response = await billingService.resendGroupLink(transactionId);
      
      if (response.success) {
        setSuccess('Link do grupo reenviado com sucesso!');
        setTimeout(() => setSuccess(''), 5000);
      } else {
        setError(response.error || 'Erro ao reenviar link do grupo');
        setTimeout(() => setError(''), 5000);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao reenviar link do grupo');
      setTimeout(() => setError(''), 5000);
    } finally {
      setResendingLink(prev => ({ ...prev, [transactionId]: false }));
    }
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined || isNaN(value)) {
      return 'R$ 0,00';
    }
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };


  const getChartConfig = () => {
    if (!chartData || chartData.length === 0) return null;

    return {
      labels: chartData.map(item => item.month_label),
      datasets: [
        {
          label: 'Faturamento Mensal (R$)',
          data: chartData.map(item => item.total || 0),
          borderColor: '#22c55e',
          backgroundColor: 'rgba(34, 197, 94, 0.1)',
          fill: true,
          tension: 0.4,
          pointBackgroundColor: '#22c55e',
          pointBorderColor: '#ffffff',
          pointBorderWidth: 2,
          pointRadius: 5,
          pointHoverRadius: 7
        }
      ]
    };
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top',
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            return `R$ ${context.parsed.y.toFixed(2)}`;
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: function(value) {
            return 'R$ ' + value.toFixed(2);
          }
        }
      }
    }
  };

  if (loading && !monthlyBilling) {
    return (
      <Layout>
        <div className="billing-page">
          <div className="dashboard-loading" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '400px' }}>
            <MoonLoader color="#487fff" size={40} />
            <p style={{ marginTop: '16px', color: '#6b7280' }}>{t('billing.loading', 'Carregando dados de faturamento...')}</p>
          </div>
        </div>


      </Layout>
    );
  }

  return (
    <Layout>
      <div className="billing-page">
        
        {error && <div className="alert alert-error">{error}</div>}
        {success && <div className="alert alert-success">{success}</div>}

        {/* Resumo Mensal */}
        {monthlyBilling && (
          <div className="billing-summary">
            <div className="summary-card">
              <div className="summary-icon monthly">📅</div>
              <div className="summary-content">
                <div className="summary-label">{t('billing.monthly_billing', 'Faturamento do Mês')}</div>
                <div className="summary-value">
                  {formatCurrency(monthlyBilling.total || 0)}
                </div>
                <div className="summary-period">{monthlyBilling.period.month}</div>
              </div>
            </div>
            <div className="summary-card">
              <div className="summary-icon transactions">💳</div>
              <div className="summary-content">
                <div className="summary-label">{t('billing.transactions', 'Transações')}</div>
                <div className="summary-value">{monthlyBilling.transaction_count}</div>
                <div className="summary-period">{t('billing.this_month', 'Este mês')}</div>
              </div>
            </div>
          </div>
        )}

        {/* Currency & Country Breakdown */}
        {((currencyBreakdown && currencyBreakdown.length > 1) || (countryBreakdown && countryBreakdown.length > 0)) && (
          <div className="billing-summary-details" style={{ marginBottom: '24px' }}>
            {currencyBreakdown && currencyBreakdown.length > 1 && (
              <div className="summary-detail-card">
                <h3>{t('billing.by_currency', 'Por Moeda')}</h3>
                {currencyBreakdown.map((item) => (
                  <div key={item.currency} className="detail-item">
                    <span className="detail-label">{item.currency}</span>
                    <span className="detail-value">
                      {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: item.currency || 'BRL' }).format(item.total)} ({item.count} transações)
                    </span>
                  </div>
                ))}
              </div>
            )}
            {countryBreakdown && countryBreakdown.length > 0 && (
              <div className="summary-detail-card">
                <h3>{t('billing.by_country', 'Por País')}</h3>
                {countryBreakdown.slice(0, 10).map((item) => (
                  <div key={item.country} className="detail-item">
                    <span className="detail-label">{item.country}</span>
                    <span className="detail-value">
                      {formatCurrency(item.total)} ({item.count} transações)
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Filtros */}
        <div className="billing-filters">
          <h2>{t('billing.query_filters', 'Filtros de Consulta')}</h2>
          <div className="filters-grid">
            <div className="filter-group">
              <label>{t('billing.start_date', 'Data Inicial')}</label>
              <DatePicker
                selected={filters.start_date ? new Date(filters.start_date + 'T12:00:00') : null}
                onChange={(date) => {
                  handleFilterChange('start_date', date ? date.toISOString().split('T')[0] : '');
                }}
                dateFormat="dd/MM/yyyy"
                locale="pt-BR"
                placeholderText="Selecione a data"
                className="date-picker-input"
                isClearable
                maxDate={filters.end_date ? new Date(filters.end_date + 'T12:00:00') : null}
              />
            </div>
            <div className="filter-group">
              <label>{t('billing.end_date', 'Data Final')}</label>
              <DatePicker
                selected={filters.end_date ? new Date(filters.end_date + 'T12:00:00') : null}
                onChange={(date) => {
                  handleFilterChange('end_date', date ? date.toISOString().split('T')[0] : '');
                }}
                dateFormat="dd/MM/yyyy"
                locale="pt-BR"
                placeholderText="Selecione a data"
                className="date-picker-input"
                isClearable
                minDate={filters.start_date ? new Date(filters.start_date + 'T12:00:00') : null}
              />
            </div>
            <div className="filter-group">
              <label>{t('billing.bot', 'Bot')}</label>
              <select
                value={filters.bot_id}
                onChange={(e) => handleFilterChange('bot_id', e.target.value)}
              >
                <option value="">{t('billing.all_bots', 'Todos os bots')}</option>
                {bots.map(bot => (
                  <option key={bot.id} value={bot.id}>{bot.name}</option>
                ))}
              </select>
            </div>
            <div className="filter-group">
              <label>{t('billing.payment_method', 'Método de Pagamento')}</label>
              <select
                value={filters.payment_method}
                onChange={(e) => handleFilterChange('payment_method', e.target.value)}
              >
                <option value="">{t('common.all', 'Todos')}</option>
                <option value="credit_card">{t('billing.credit_card', 'Cartão de Crédito')}</option>
                <option value="pix">{t('billing.pix', 'PIX')}</option>
              </select>
            </div>
            <div className="filter-group">
              <label>{t('billing.gateway', 'Gateway')}</label>
              <select
                value={filters.gateway}
                onChange={(e) => handleFilterChange('gateway', e.target.value)}
              >
                <option value="">{t('common.all', 'Todos')}</option>
                <option value="mercadopago">Mercado Pago</option>
                <option value="stripe">Stripe</option>
              </select>
            </div>
          </div>
          <div className="filters-actions">
            <button onClick={clearFilters} className="btn-clear-filters">
              {t('billing.clear_filters', 'Limpar Filtros')}
            </button>
          </div>
        </div>

        {/* Gráfico */}
        {chartData && chartData.length > 0 && (
          <div className="billing-chart-section">
            <h2>{t('billing.billing_evolution', 'Evolução do Faturamento (Últimos 12 Meses)')}</h2>
            <div className="chart-container">
              {getChartConfig() && (
                <Line data={getChartConfig()} options={chartOptions} />
              )}
            </div>
          </div>
        )}

        {/* Resumo de Planos e Assinaturas */}
        {billingData && billingData.transactions && billingData.transactions.length > 0 && (
          <div className="billing-plans-section">
            <div className="section-header">
              <h2>{t('billing.plans_and_subscriptions', 'Planos e Assinaturas')}</h2>
            </div>
            
            <div className="plans-subscriptions-grid">
              {/* Resumo por Plano */}
              {billingData.plans && billingData.plans.length > 0 ? (
                <div className="plans-card">
                  <h3>{t('billing.revenue_by_plan', 'Recebimentos por Plano')}</h3>
                  <div className="plans-list">
                    {billingData.plans.map((plan, index) => (
                      <div key={plan.plan_id || index} className="plan-item">
                        <div className="plan-header">
                          <span className="plan-title">{plan.plan_title}</span>
                          <span className="plan-price">{formatCurrency(plan.plan_price)}</span>
                        </div>
                        <div className="plan-stats">
                          <span className="stat-item">
                            <strong>{plan.subscription_count}</strong> {t('billing.subscriptions_count', 'assinatura(s)')}
                          </span>
                          <span className="stat-item">
                            Total: <strong>{formatCurrency(plan.total_revenue)}</strong>
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="plans-card">
                  <h3>{t('billing.revenue_by_plan', 'Recebimentos por Plano')}</h3>
                  <div className="empty-state">
                    <p>{t('billing.no_plans_found', 'Nenhum plano encontrado')}</p>
                  </div>
                </div>
              )}

              {/* Resumo por Assinatura */}
              {billingData.subscriptions && billingData.subscriptions.length > 0 ? (
                <div className="subscriptions-card">
                  <h3>{t('billing.active_subscriptions', 'Assinaturas Ativas')}</h3>
                  <div className="subscriptions-list">
                    {billingData.subscriptions.slice(0, 10).map((subscription, index) => (
                      <div key={index} className="subscription-item">
                        <div className="subscription-header">
                          <span className="subscription-user">
                            {subscription.contact_name}
                            {subscription.contact_username && (
                              <span className="subscription-username">@{subscription.contact_username}</span>
                            )}
                          </span>
                          <span className="subscription-plan">{subscription.plan_title}</span>
                        </div>
                        <div className="subscription-details">
                          <span className="detail-item">
                            <strong>{subscription.transaction_count}</strong> pagamento(s)
                          </span>
                          <span className="detail-item">
                            Ciclo: <strong>{subscription.cycle_name}</strong> ({subscription.cycle_days} dias)
                          </span>
                          <span className="detail-item">
                            Total: <strong>{formatCurrency(subscription.total_revenue)}</strong>
                          </span>
                        </div>
                        <div className="subscription-dates">
                          <small>Primeiro pagamento: {subscription.first_payment}</small>
                          <small>Último pagamento: {subscription.last_payment}</small>
                        </div>
                      </div>
                    ))}
                    {billingData.subscriptions.length > 10 && (
                      <div className="subscriptions-more">
                        <small>E mais {billingData.subscriptions.length - 10} assinatura(s)...</small>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="subscriptions-card">
                  <h3>{t('billing.active_subscriptions', 'Assinaturas Ativas')}</h3>
                  <div className="empty-state">
                    <p>{t('billing.no_subscriptions_found', 'Nenhuma assinatura encontrada')}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Tabela de Transações */}
        {billingData && (
          <div className="billing-transactions-section">
            <div className="section-header">
              <h2>{t('billing.transactions', 'Transações')}</h2>
              {billingData.summary && (
                <div className="summary-badge">
                  Total: {formatCurrency(billingData.summary.total)} ({billingData.summary.transaction_count} transações)
                </div>
              )}
            </div>

            {billingData.transactions && billingData.transactions.length > 0 ? (
              <>
                <div className="transactions-table-container">
                  <table className="transactions-table">
                    <thead>
                      <tr>
                        <th>{t('billing.date', 'Data')}</th>
                        <th>{t('billing.user', 'Usuário')}</th>
                        <th>{t('billing.bot', 'Bot')}</th>
                        <th>{t('billing.plan', 'Plano')}</th>
                        <th>{t('billing.method', 'Método')}</th>
                        <th>{t('billing.gateway', 'Gateway')}</th>
                        <th>{t('billing.amount', 'Valor')}</th>
                        <th>{t('common.status', 'Status')}</th>
                        <th>{t('common.actions', 'Ações')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {billingData.transactions.map((transaction) => (
                        <tr key={transaction.id}>
                          <td>{transaction.created_at_formatted}</td>
                          <td>
                            <div className="user-cell">
                              <div className="user-name">{transaction.contact.name}</div>
                              {transaction.contact.username && (
                                <div className="user-username">@{transaction.contact.username}</div>
                              )}
                            </div>
                          </td>
                          <td>{transaction.bot.name}</td>
                          <td>
                            <div className="plan-cell">
                              <div className="plan-name">{transaction.payment_plan?.title || 'N/A'}</div>
                              <div className="plan-price-small">{formatCurrency(transaction.payment_plan?.price || 0)}</div>
                            </div>
                          </td>
                          <td>
                            <span className={`payment-method-badge ${transaction.payment_method}`}>
                              {transaction.payment_method === 'credit_card' ? '💳 Cartão' : '💰 PIX'}
                            </span>
                          </td>
                          <td>
                            <span className="gateway-badge">{transaction.gateway}</span>
                          </td>
                          <td className="amount-cell">{formatCurrency(transaction.amount)}</td>
                          <td>
                            <span className={`status-badge ${transaction.status}`}>
                              {transaction.status === 'approved' || transaction.status === 'paid' || transaction.status === 'completed' 
                                ? '✅ Aprovado' 
                                : transaction.status}
                            </span>
                          </td>
                          <td>
                            {(transaction.status === 'approved' || transaction.status === 'paid' || transaction.status === 'completed') && (
                              <button
                                className="btn btn-sm btn-success"
                                onClick={() => handleResendGroupLink(transaction.id)}
                                disabled={resendingLink[transaction.id]}
                                title={t('billing.resend_group_link', 'Reenviar link do grupo para o usuário')}
                              >
                                <FontAwesomeIcon icon={faPaperPlane} spin={resendingLink[transaction.id]} />
                                {resendingLink[transaction.id] ? ` ${t('billing.sending', 'Enviando...')}` : ` ${t('billing.resend_link', 'Reenviar Link')}`}
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Resumo por Método e Gateway */}
                {billingData.summary && (
                  <div className="billing-summary-details">
                    <div className="summary-detail-card">
                      <h3>{t('billing.by_payment_method', 'Por Método de Pagamento')}</h3>
                      {Object.entries(billingData.summary.by_payment_method).map(([method, data]) => (
                        <div key={method} className="detail-item">
                          <span className="detail-label">
                            {method === 'credit_card' ? '💳 Cartão de Crédito' : '💰 PIX'}
                          </span>
                          <span className="detail-value">
                            {formatCurrency(data.total)} ({data.count} transações)
                          </span>
                        </div>
                      ))}
                    </div>
                    <div className="summary-detail-card">
                      <h3>{t('billing.by_gateway', 'Por Gateway')}</h3>
                      {Object.entries(billingData.summary.by_gateway).map(([gateway, data]) => (
                        <div key={gateway} className="detail-item">
                          <span className="detail-label">{gateway}</span>
                          <span className="detail-value">
                            {formatCurrency(data.total)} ({data.count} transações)
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="empty-state">
                <p>{t('billing.no_transactions_found', 'Nenhuma transação encontrada com os filtros aplicados.')}</p>
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Billing;
